import { type EmbedTarget } from './embed-targets/embed-target'
import { type EmbedTargetFactory } from './embed-targets/embed-target-factory'
import { logger } from '../utils/logger'
import { nanoid } from 'nanoid'
import { type EmbedSettings } from './settings'
import { detectEmbedTargetFromContext } from './embed-targets/utils'
import { type LocalEmbedEventEmitter } from './event-handler'
import { type PlayerJsContext } from './hooks/playerjs'
import { type EmbedderContext } from './embedder'
import { url as ExperienceUtilsUrl } from '@source-digital/experience-utils'
import { stringOrDomValueExtract } from '../utils/dom-value-extract'
import { PlayerjsExperienceProtocol, type ExperienceProtocol } from './experience/experience-protocol'
import { type EmbedderSettings } from './internals/types'
import { generateSelectorFromAttributes, injectConditionalSheet } from '../utils/dom-style'

const { ExperienceUrlBuilder } = ExperienceUtilsUrl

/**
 * A context per each embedding instance before an iframe element is created
 */
export interface EmbedBeforeCreateElementContext extends EmbedderContext {
  settings: ElementEmbedderSettings
  /**
   * an unique id of the embedding instance
   */
  id: string
  /**
   * an HTMLElement embedding was performed against
   */
  targetEl: HTMLElement
  /**
   * embedded experience URL
   */
  url: string
  /**
   * embedded experience distribution ID
   */
  distributionId?: string | null
  additionalProperties: Map<string, any>
  /**
   * used for communications between hooks
   */
  eventEmitter: LocalEmbedEventEmitter
  /**
   * shows the embedded experience
   */
  show (): void
  /**
   * hides the embedded experience
   */
  hide (): void
}
/**
 * a context per each embedding instance
 */
export interface EmbedElementContext extends EmbedBeforeCreateElementContext {

  /**
   * embed handler for {@link targetEl}
   */
  embedTarget?: EmbedTarget
  /**
   * a reference to the player.js instance attached to the {@link iframeEl}
   */
  playerjs?: PlayerJsContext

  teleportActive?: boolean

  /**
   * a communication tool between embedder and embedded experience
   * wrapper around eventEmitter/player.js
   */
  experienceProtocol: ExperienceProtocol
}

export function getEnvUrl (env?: string): string {
  if (env == null || env === 'prod') return 'https://experience.sourcesync.io'
  if (['dev', 'stg'].includes(env)) return `https://experience-${env}.sourcesync.io`
  return env
}

export function createDistributionUrl (settings: EmbedSettings, id: string): string {
  const urlBuilder = new ExperienceUrlBuilder(getEnvUrl(settings.env))
  let data

  // we need to carefully pass the distributionId as id to the urlBuilder, only if it is truthy
  if (settings.distributionId != null) {
    data = { id: settings.distributionId }
  } else if (settings.organizationId != null && settings.clientId != null) {
    data = { organizationId: settings.organizationId, clientId: settings.clientId }
  } else {
    throw new Error('Either distributionId or organizationId and clientId must be provided.')
  }

  const url = urlBuilder.createDistributionUrl(data, { settings: {} })
  url.searchParams.set('embed', 'true')
  url.searchParams.set('embedId', id)
  if (settings.overlayTimeSync === true) url.searchParams.set('settings.layout.template.options.disableTimer', 'true')
  if (settings.debug != null && settings.debug !== false) {
    url.searchParams.set('debug', 'true') // FIXME deprecated?
    url.searchParams.set('settings.debug.enabled', 'true')
  }
  if (settings.debug != null && typeof settings.debug === 'object') {
    Object.entries(settings.debug).forEach(([key, value]) => {
      url.searchParams.set(`settings.debug.${key}`, `${Boolean(value).toString()}`)
    })
  }
  // disable bottom space inside ee. The bottom space will be computed by embedder
  url.searchParams.set('settings.behavior.disableBottomSpace', 'true')

  // TODO move this to EmbedStrategy somehow
  if (settings.strategy === 'direct' || settings.strategy === 'direct-with-iframe') {
    url.searchParams.set('settings.renderless', 'true')
  }
  return url.toString()
}

// TODO:Enhance this
type ElementEmbedderSettings = EmbedderSettings // WithRequiredProperty<EmbedderSettings, 'distributionId' | 'organizationId' | 'clientId'>

function validateSettings (targetEl: Element, embedSettings: EmbedderSettings): ElementEmbedderSettings {
  const distributionId = embedSettings.distributionId ?? (embedSettings.attribute != null ? targetEl.getAttribute(embedSettings.attribute) : '')
  const organizationId = embedSettings.organizationId
  const clientId = embedSettings.clientId != null ? stringOrDomValueExtract(embedSettings.clientId) : undefined

  let validated = false

  if (!validated && organizationId != null && clientId != null) {
    validated = true
  }

  if (!validated && distributionId == null) {
    // TODO enhance this error
    throw new Error('Could not detect valid distribution identifier.')
  }

  return {
    ...embedSettings,
    distributionId,
    clientId: clientId ?? undefined
  }
}

/**
 * Manages an instance of embedding against single element.
 */
export class ElementEmbedder {
  readonly id = nanoid()
  ctx: EmbedderContext
  targetEl: HTMLElement
  settings: ElementEmbedderSettings
  defaultEmbedTargetFactory?: EmbedTargetFactory<EmbedTarget>
  elemIdAttr: string
  url: string
  eventEmitter: LocalEmbedEventEmitter

  constructor (ctx: EmbedderContext, targetEl: HTMLElement, settings: EmbedderSettings, defaultEmbedTargetFactory?: EmbedTargetFactory<EmbedTarget>) {
    this.ctx = ctx
    this.targetEl = targetEl
    this.settings = validateSettings(targetEl, settings)
    this.defaultEmbedTargetFactory = defaultEmbedTargetFactory
    this.elemIdAttr = `${this.settings.attribute}-id`
    this.url = createDistributionUrl(this.settings, this.id)
    this.eventEmitter = this.ctx.globalEventEmitter.getRoom(this.id)
  }

  #findEmbedTargetFactory (beforeContext: EmbedBeforeCreateElementContext, targetEl: HTMLElement): EmbedTargetFactory<EmbedTarget> {
    const defaultEmbedTargetFactory = this.defaultEmbedTargetFactory
    // If targetType is specified, check if it can be used. If not, auto detect
    let _embedTargetFactory: EmbedTargetFactory<EmbedTarget> | undefined = defaultEmbedTargetFactory
    if (_embedTargetFactory != null && !_embedTargetFactory.supports(beforeContext)) {
      throw new Error('This target element is not supported by selected targetType. type' + (this.settings.targetType ?? ''))
    }

    if (_embedTargetFactory == null) {
      _embedTargetFactory = detectEmbedTargetFromContext(beforeContext)
      if (_embedTargetFactory == null) {
        logger.warn('This target element is not supported.', targetEl)
      }
    }
    if (_embedTargetFactory == null) throw new Error('Could not find a suitable embed target factory.')
    return _embedTargetFactory
  }

  #createBeforeCtx (): EmbedBeforeCreateElementContext {
    return {
      ...this.ctx,
      id: this.id,
      settings: this.settings,
      url: this.url,
      targetEl: this.targetEl,
      distributionId: this.settings.distributionId,
      additionalProperties: new Map(),
      eventEmitter: this.eventEmitter,
      show (): void {
        // Consider: should this.embedTarget have show/hide methods?
        this.embedTarget.embedStrategy.show()
      },
      hide (): void {
        // Consider: should this.embedTarget have show/hide methods?
        this.embedTarget.embedStrategy.hide()
      }
    }
  }

  #injectCSSRules () {
    const _sheets = this.settings.css?.sheets
    if (_sheets == null) return
    const sheets = Array.isArray(_sheets) ? _sheets : Object.values(_sheets)
    const componentSelector = generateSelectorFromAttributes(this.ctx.getComponentMarkerAttributes())
    for (const sheet of sheets) {
      if (sheet != null) injectConditionalSheet(sheet, componentSelector)
    }
  }

  async #embedIframe (url: string, factory: EmbedTargetFactory<EmbedTarget>, beforeCtx: EmbedBeforeCreateElementContext) {
    this.targetEl.setAttribute(this.elemIdAttr, this.id)
    const embedCtx: EmbedElementContext = {
      ...beforeCtx,
      experienceProtocol: new PlayerjsExperienceProtocol(beforeCtx.eventEmitter)
    }
    const embedTarget = factory.create(embedCtx)
    embedCtx.embedTarget = embedTarget
    await embedTarget.embed()
    logger.debug(`Embedder "${this.ctx.settings.scope}" embedded. Embed type: ${embedTarget.name}`, embedCtx.targetEl)
    // @ts-expect-error Injecting embedCtx into targetEl for debugging purposes. Also, don't use this.
    this.targetEl.__sd_embed__ = embedCtx
    return embedCtx
  }

  async embed () {
    const targetEl = this.targetEl

    if (!(targetEl instanceof HTMLElement)) {
      logger.warn('This element is not supported.', targetEl)
      return null
    }

    if (targetEl.getAttribute(this.elemIdAttr) !== null) {
      logger.warn('This element is already instantiated.', targetEl)
      return null
    }

    logger.debug(`Embedder "${this.settings.scope}" assigned distributionId="${this.settings.distributionId as string}" id="${this.id}"`)

    const beforeContext: EmbedBeforeCreateElementContext = this.#createBeforeCtx()

    const factory: EmbedTargetFactory<EmbedTarget> = this.#findEmbedTargetFactory(beforeContext, targetEl)

    this.ctx.hooks.forEach(hook => { hook.onBeforeCreateElement?.(beforeContext) })
    this.#injectCSSRules()
    const embedCtx: EmbedElementContext = await this.#embedIframe(this.url, factory, beforeContext)
    this.ctx.hooks.forEach(hook => { hook.onAfterCreateElement?.(embedCtx) })

    this.ctx.embeddedList.push(embedCtx)

    return embedCtx
  }

  destroy () {
    // @ts-expect-error Cleaning up to avoid memory leak
    delete this.targetEl.__sd_embed__
  }
}
